import React, { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useOutletContext } from 'react-router-dom';

import OperatorHeader from '../../components/Operator/OperatorHeader';
import OperatorFilters from '../../components/Operator/OperatorFilters';
import OperatorTable from '../../components/Operator/OperatorTable';
import OperatorCreateDialog from '../../components/Edit/OperatorCreateDialog';

import CenteredLoader from '../../../../components/loaders/PageLoader';
import styles from './Operator.styles';

import { useOperators } from '../../hooks/useOperators';
import { filterOperators } from '../../utils/operators.helpers';
import exportToCsv from '../../../../utils/exportToCsv';

export default function Operator() {
  const { isSidebarExpanded } = useOutletContext();

  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({ name: [], operator: [], error: [] });
  const [createOpen, setCreateOpen] = useState(false);

  const { rows, setRows, loading, refreshing } = useOperators();

  const filteredRows = useMemo(
    () => filterOperators(rows, filters, searchQuery),
    [rows, filters, searchQuery]
  );

  const nameOptions = useMemo(() => [...new Set(rows.map((r) => r.name))], [rows]);
  const operatorOptions = useMemo(() => [...new Set(rows.map((r) => r.description))], [rows]);

  const openCreate = () => setCreateOpen(true);
  const closeCreate = () => setCreateOpen(false);

  const onCreateSubmit = async (created) => {
    try {
      const newOp = created;
      setRows((prev) => [
        ...prev,
        {
          name: newOp.Description,
          description: newOp.Code,
          unitCount: newOp.UnitCount,
          meterCount: newOp.MeterCount,
          evnCount: newOp.EVNCount,
          errorsCount: newOp.ErrorsCount || 0,
        },
      ]);
      closeCreate();
    } catch (err) {
      console.error('Error handling created operator:', err);
    }
  };

  const handleExport = useCallback(() => {
    const ts = new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '').replace('T', '_');
    const filename = `operators_${ts}.csv`;
    const rowsToExport = filteredRows.map(({ errorsCount, ...rest }) => rest);
    const headerMap = {
      name: 'Name',
      description: 'Code',
      unitCount: 'Unit Count',
      meterCount: 'Meter Count',
      evnCount: 'EVN Count',
    };
    exportToCsv(filename, rowsToExport, headerMap);
  }, [filteredRows]);

  return (
    <Box
      sx={{
        ...styles.page,
        marginLeft: isSidebarExpanded ? '240px' : '95px',
        width: isSidebarExpanded ? 'calc(100vw - 240px)' : 'calc(100vw - 95px)',
        transition: 'all 0.3s ease',
      }}
    >
      {loading ? (
        <CenteredLoader />
      ) : (
        <>
          <OperatorHeader
            title="Operators"
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onSearch={() => {}}
          />

          <OperatorFilters
            filters={filters}
            setFilters={setFilters}
            nameOptions={nameOptions}
            operatorOptions={operatorOptions}
            onAdd={openCreate}
            onExport={handleExport}
          />

          <OperatorTable rows={filteredRows} refreshing={refreshing} />

          <OperatorCreateDialog open={createOpen} onClose={closeCreate} onSubmit={onCreateSubmit} />
        </>
      )}
    </Box>
  );
}
